import { baseApi } from 'src/api/baseApi';
import { camelToSnake, snakeToCamel } from 'src/utils/common';

export interface IdentityClient {
  tokenType: string;
  tokenSubject: string;
  expiresIn: number;
  csrfCode: string;
  client: {
    clientId: string;
    redirectUri: string;
    homeUri: string;
    status: string;
    title: string;
    description: string;
    ranking: string;
    logo: string;
    locale: string;
  };
}

export interface AuthorizedUser {
  tokenType: string;
  tokenSubject: string;
  expiresIn: number;
  user: {
    account: string;
    status: string;
    name: string;
    avatar: string;
    ownerType: string;
  };
}

/**
 * The current SSO session, from `GET /security/session`.
 *
 * The session lives in an HttpOnly cookie that this app cannot read, so the
 * server is the only authority on whether one exists. There is deliberately no
 * token here.
 */
export interface CurrentSession {
  account: string;
  expiresIn: number;
}

export interface IdentityClientQuery {
  clientId: string;
  redirectUri: string;
  locale: string;
}

export interface AuthorizeUserRequest {
  account: string;
  password: string;
  rememberMe: boolean;
}

interface AuthorizeUserMutationRequest extends AuthorizeUserRequest {
  csrfCode: string;
}

export const authApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getIdentityClient: builder.query<IdentityClient, IdentityClientQuery>({
      query: ({ clientId, redirectUri, locale }) => ({
        url: `/account/v1/security/identity/${encodeURIComponent(clientId)}`,
        params: { redirect_uri: redirectUri, locale },
      }),
      transformResponse: (response: unknown) => snakeToCamel<IdentityClient>(response),
    }),
    authorizeUser: builder.mutation<AuthorizedUser, AuthorizeUserMutationRequest>({
      query: ({ csrfCode, ...body }) => ({
        url: '/account/v1/security/authorize',
        method: 'POST',
        body: camelToSnake(body),
        headers: {
          'X-CSRF-Token': csrfCode,
        },
      }),
      transformResponse: (response: unknown) => snakeToCamel<AuthorizedUser>(response),
    }),
    getCurrentSession: builder.query<CurrentSession, void>({
      query: () => ({ url: '/account/v1/security/session' }),
      transformResponse: (response: unknown) => snakeToCamel<CurrentSession>(response),
    }),
    endSession: builder.mutation<void, void>({
      query: () => ({ url: '/account/v1/security/session', method: 'DELETE' }),
    }),
  }),
});

export const {
  useGetIdentityClientQuery,
  useAuthorizeUserMutation,
  useGetCurrentSessionQuery,
  useEndSessionMutation,
} = authApi;
